import { useMemo, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import type { ApiEndpoints } from '@avisoma/shared';
import { formatDateTime, formatDateTimeWithSeconds } from '../../lib/dateFormat';
import {
  OUTCOME_SERIES,
  eventTypeSeries,
  sessionSeries,
  toChartRows,
  type Series,
  type ChartRow,
  type StatsResponse,
} from './webhookDeliveryChartSeries';

// How the stacked series is split: by delivery outcome, by webhook event type,
// or by retry/upload session. Derived from the shared `groupBy` query param so
// it can't drift from the contract. Series/color derivation for each mode lives
// in `webhookDeliveryChartSeries`.
export type Breakdown = NonNullable<
  NonNullable<ApiEndpoints<string>['/api/projects/:id/webhook-deliveries/stats']['GET']['query']>['groupBy']
>;

const DAY_MS = 86_400_000;

function tickFormatter(bucketSeconds: number, spanMs: number) {
  // Day-wide buckets label by calendar day. Finer buckets label by time — with
  // seconds when the bucket is sub-minute (else adjacent ticks collapse to the
  // same HH:MM), and prefixed with the date when the window spans more than a
  // day (else bare times repeat across days, common now with auto-zoom).
  const timeOpts: Intl.DateTimeFormatOptions =
    bucketSeconds < 60
      ? { hour: '2-digit', minute: '2-digit', second: '2-digit' }
      : { hour: '2-digit', minute: '2-digit' };
  return (value: string) => {
    const d = new Date(value);
    // Day-wide buckets are epoch-anchored to UTC midnight, so label them in UTC —
    // in the viewer's local tz a west-of-UTC offset would render each bucket's
    // midnight as the previous calendar day. Sub-daily buckets label an exact
    // instant, so those stay in local tz (below).
    if (bucketSeconds >= 86400) {
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
    }
    const time = d.toLocaleTimeString(undefined, timeOpts);
    return spanMs > DAY_MS
      ? `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${time}`
      : time;
  };
}

function ChartTooltip({
  active,
  payload,
  series,
  bucketSeconds,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartRow }>;
  series: Series[];
  bucketSeconds: number;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  // Match the tick semantics: day-wide buckets are UTC calendar days (epoch
  // anchor), so the header is a UTC date — a local-tz datetime would name the
  // previous day west of UTC. Sub-minute buckets add seconds so adjacent
  // buckets stay distinguishable; in between, plain local datetime.
  const header =
    bucketSeconds >= 86400
      ? new Date(row.start).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        })
      : bucketSeconds < 60
        ? formatDateTimeWithSeconds(row.start)
        : formatDateTime(row.start);
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-md dark:border-zinc-700 dark:bg-zinc-900">
      <div className="mb-1 font-medium text-gray-900 dark:text-zinc-100">{header}</div>
      <div className="space-y-0.5">
        {series.map((s) => (
          <div key={s.key} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-zinc-300">
              <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: s.fill }} />
              {s.label}
            </span>
            <span className="font-mono text-gray-900 dark:text-zinc-100">{Number(row[s.key] ?? 0)}</span>
          </div>
        ))}
        <div className="mt-1 flex items-center justify-between gap-4 border-t border-gray-100 pt-1 dark:border-zinc-700">
          <span className="text-gray-600 dark:text-zinc-300">Total</span>
          <span className="font-mono text-gray-900 dark:text-zinc-100">{row.total}</span>
        </div>
      </div>
    </div>
  );
}

const BREAKDOWN_OPTIONS: { value: Breakdown; label: string }[] = [
  { value: 'outcome', label: 'Outcome' },
  { value: 'eventType', label: 'Event type' },
  { value: 'session', label: 'Session' },
];

// Segmented button group used by the breakdown toggle. `value` may be null (no
// option active), kept for forward flexibility.
function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T | null;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex overflow-hidden rounded-md border border-gray-200 text-xs dark:border-zinc-600">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-2.5 py-1 transition-colors ${
            value === opt.value
              ? 'bg-indigo-500 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-600'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export type Zoom = { from: string; to: string };

export function WebhookDeliveriesChart({
  breakdown,
  onBreakdownChange,
  zoom,
  onZoomChange,
  data,
  isLoading,
  isPlaceholderData,
}: {
  // Chart-only display toggle (drives the server groupBy). Deliberately does not
  // touch the table, chips, or filter state.
  breakdown: Breakdown;
  onBreakdownChange: (breakdown: Breakdown) => void;
  // Zoom is lifted to the parent so the deliveries table and the event-type chip
  // row share the same window and single stats fetch. With no zoom the parent
  // requests `range=auto` (full delivery span).
  zoom: Zoom | null;
  onZoomChange: (zoom: Zoom | null) => void;
  // Stats query state owned by the parent — the chart is presentational.
  data: StatsResponse | undefined;
  isLoading: boolean;
  isPlaceholderData: boolean;
}) {
  // Drag-selection endpoints, expressed as bucket-start labels of the current
  // window (null when not dragging). State drives the ReferenceArea render;
  // the refs are the source of truth for commit — a fast drag can release
  // before React re-renders, so the mouseup closure would otherwise read
  // stale (null) endpoints and silently drop the zoom.
  const [dragStart, setDragStart] = useState<string | null>(null);
  const [dragEnd, setDragEnd] = useState<string | null>(null);
  const dragStartRef = useRef<string | null>(null);
  const dragEndRef = useRef<string | null>(null);

  // Derive the series from the grouping the DATA was actually fetched with, not
  // the toggle state — while keepPreviousData holds a previous grouping's buckets
  // through a refetch, this keeps the count keys interpreted correctly (no
  // outcome-keys-as-event-types flash, no bogus "No deliveries in range").
  const effectiveBreakdown = data?.groupBy ?? breakdown;
  const series = useMemo(() => {
    if (effectiveBreakdown === 'session') return sessionSeries(data?.series ?? []);
    if (effectiveBreakdown === 'eventType') return eventTypeSeries(data?.buckets ?? []);
    return OUTCOME_SERIES;
  }, [effectiveBreakdown, data]);
  const rows = useMemo(() => (data ? toChartRows(data.buckets, series) : []), [data, series]);
  const hasDeliveries = rows.some((r) => r.total > 0);
  const bucketSeconds = data?.bucketSeconds ?? 3600;
  const spanMs = data ? Date.parse(data.to) - Date.parse(data.from) : 0;

  const cancelDrag = () => {
    dragStartRef.current = null;
    dragEndRef.current = null;
    setDragStart(null);
    setDragEnd(null);
  };

  const commitZoom = (releaseLabel?: string | number) => {
    const start = dragStartRef.current;
    // Prefer the mouseup event's own label — it's fresher than the last mousemove.
    const end = releaseLabel != null ? String(releaseLabel) : dragEndRef.current;
    if (start != null && end != null && start !== end) {
      const lo = Math.min(Date.parse(start), Date.parse(end));
      // Extend by one bucket so the last-selected bar is included in the window.
      const hi = Math.max(Date.parse(start), Date.parse(end)) + bucketSeconds * 1000;
      onZoomChange({ from: new Date(lo).toISOString(), to: new Date(hi).toISOString() });
    }
    cancelDrag();
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow dark:border-zinc-700 dark:bg-zinc-800 dark:shadow-zinc-950/30">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100">Delivery volume</h2>
        <div className="flex items-center gap-2">
          <SegmentedControl options={BREAKDOWN_OPTIONS} value={breakdown} onChange={onBreakdownChange} />
          {/* Zoom replaces the auto full-span view; clearing it returns to auto. */}
          {zoom && (
            <button
              type="button"
              onClick={() => onZoomChange(null)}
              className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-600"
            >
              Reset zoom
            </button>
          )}
        </div>
      </div>

      {/* keepPreviousData holds the prior series while a new range/filter/project
          loads (no blink on toggle) — dim it so stale buckets aren't misread as
          current. select-none so drag-to-zoom doesn't select surrounding text. */}
      <div
        className={`relative h-56 w-full select-none transition-opacity ${isPlaceholderData ? 'opacity-40' : ''}`}
      >
        {isLoading && !data ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-zinc-400">
            Loading…
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                accessibilityLayer={false}
                data={rows}
                margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                onMouseDown={(s: { activeLabel?: string | number }) => {
                  const label = s?.activeLabel != null ? String(s.activeLabel) : null;
                  dragStartRef.current = label;
                  dragEndRef.current = null;
                  setDragStart(label);
                  setDragEnd(null);
                }}
                onMouseMove={(s: { activeLabel?: string | number }) => {
                  if (dragStartRef.current != null && s?.activeLabel != null) {
                    const label = String(s.activeLabel);
                    dragEndRef.current = label;
                    setDragEnd(label);
                  }
                }}
                onMouseUp={(s: { activeLabel?: string | number }) => commitZoom(s?.activeLabel)}
                // Dragging past the chart edge commits the selection made so far
                // (cancelling instead would silently discard an overshot drag).
                onMouseLeave={() => commitZoom()}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" vertical={false} />
                <XAxis
                  dataKey="start"
                  tick={{ fontSize: 11 }}
                  tickFormatter={tickFormatter(bucketSeconds, spanMs)}
                  minTickGap={40}
                  className="fill-zinc-500 dark:fill-zinc-400"
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} className="fill-zinc-500 dark:fill-zinc-400" />
                {/* offset: keep the tooltip clear of the pointer so it doesn't obscure the hovered bar */}
                <Tooltip
                  cursor={{ fill: 'rgba(113,113,122,0.12)' }}
                  offset={28}
                  content={<ChartTooltip series={series} bucketSeconds={bucketSeconds} />}
                />
                {series.map((s) => (
                  <Bar key={s.key} dataKey={s.key} stackId="a" fill={s.fill} />
                ))}
                {dragStart != null && dragEnd != null && (
                  <ReferenceArea x1={dragStart} x2={dragEnd} fill="#6366f1" fillOpacity={0.15} />
                )}
              </BarChart>
            </ResponsiveContainer>
            {!hasDeliveries && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-500 dark:text-zinc-400">
                No deliveries in range
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
